// Раздел «Настройки»: то, что человек настраивает сам, без администратора базы.
//
// Пока это адрес сервера и сведения о программе. Роли, права и справочники придут с блоком Б9,
// и об этом сказано прямо: раздел, который выглядит готовым, но ничего не умеет, обманывает
// сильнее пустого.
//
// Адрес сервера здесь дублирует панель на входе намеренно. На входе он нужен, когда войти
// нельзя; здесь — когда сервер переехал и человек уже внутри. Это два разных момента жизни,
// а не два места одной настройки.

// Полоса вкладок, а не сегментированный переключатель. Правка 03.09.2026, общая на весь продукт.
//
// Сегментированный раскладывает доли РАВНЫМИ и запрещает перенос текста: как только пунктов
// становится больше, чем помещается, названия налезают друг на друга. В «Найме» это уже
// случилось — восемь названий в колонке 416 точек рисовались одно поверх другого, — а в
// «Деньгах» стояло в шаге от этого: «Очередь приёмки» требовала 115 точек при доле в 106.
//
// Граница теперь по смыслу, а не по числу пунктов: сегментированный остаётся там, где человек
// выбирает РЕЖИМ из двух-трёх равных (порядок сортировки на пульте, способ ведения табеля);
// переход между ПРЕДСТАВЛЕНИЯМИ раздела везде идёт полосой вкладок. У неё ширина по
// содержимому и прокрутка, то есть наложение невозможно по устройству. Держит
// `e2e/переключатель-читается.spec.ts` — замером координат, а не чтением исходника: пункты
// собираются циклом по правам, и в тексте файла их не пересчитать.

import { useState } from 'react';

import { RiCheckLine, RiServerLine } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import {
  checkServerAddress,
  serverAddress,
  serverAddressIsCustom,
  setServerAddress,
} from '@/shared/api/server-address';
import { СтрокаВерсии, useОткрытьИсторию } from '@/layout/ЧтоНового';
import { useПлатформа } from '@/shared/desktop/где-мы';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { parseSearch } from '@/shared/routing/search';
import { Block } from '@/shared/ui/block';
import { SectionHeader } from '@/shared/ui/section-header';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import { РеквизитыЮрлица } from './LegalEntityRequisites';
import { ПравилаРасчёта } from './SettingsPanelBlock';
import { СправочникСпецодежды } from './UniformCatalog';
import { СправочникТиповОбъектов, СправочникТиповОформления } from './TypeCatalogs';
import { TextField } from '@/shared/ui/text-field';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';

import { AuditView } from './audit/AuditView';
import { UsersPanel } from './UsersPanel';
import { ЖалобыНаПереписку } from './ЖалобыНаПереписку';

/** Представление «Общие»: адрес сервера, правила расчёта, справочники, люди. */
export const VIEW_GENERAL = 'general';
/** Представление «Журнал действий»: кто, когда и что изменил (тз, 3.1 и 8.5). */
export const VIEW_AUDIT = 'audit';

export function SettingsSection({ view }: { view?: string }) {
  const права = useAuthStore((состояние) => состояние.permissions);
  const адрес = useLocation();
  const виденЖурнал = can(права, 'core.audit.view') || can(права, 'core.audit.view_own');
  const открытьИсторию = useОткрытьИсторию();

  if (view === VIEW_AUDIT && виденЖурнал) {
    return (
      <div className='flex h-full min-w-0 flex-1 flex-col'>
        <ПереключательПредставлений текущее={VIEW_AUDIT} виденЖурнал={виденЖурнал} />
        <AuditView search={parseSearch(адрес.search)} />
      </div>
    );
  }

  return (
    <main className='flex h-full flex-1 flex-col overflow-y-auto bg-bg-weak-50'>
      {виденЖурнал && (
        <ПереключательПредставлений текущее={VIEW_GENERAL} виденЖурнал={виденЖурнал} />
      )}
      {/* «Администратор базы» — наше слово, не его: человек в кабинете не знает, кто это и чем
          он отличается от администратора системы, к которому его тут же и отправляют строкой
          ниже. */}
      <SectionHeader title='Настройки' subtitle='Адрес сервера, люди и их права.' />

      <div className='flex flex-col gap-4 p-5'>
        <АдресСервера />

        {/* Правила расчёта стоят ВЫШЕ списка людей: настройка про деньги, а список про доступ,
          и заходят сюда чаще за первым. */}
        {can(права, 'core.setting.view') && <ПравилаРасчёта />}

        {/* Цены стоят сразу под правилами расчёта и ВЫШЕ реквизитов. Перестановка 17.09.2026
          по прямой просьбе владельца: «должна быть генеральная панель по стоимости разных
          товаров таких расходных». Блок при этом назывался «Спецодежда» — то есть человек,
          пришедший в настройки за словом «цены», не находил его ни в одном заголовке и
          пролистывал мимо. Реквизиты правят раз в год, цены — при каждом подорожании. */}
        {can(права, 'core.dictionary.view') && <СправочникСпецодежды />}

        {can(права, 'core.dictionary.view') && <РеквизитыЮрлица />}

        {/* Два справочника, от которых зависят чужие экраны: по типу оформления реестр людей
            делится на представления, от типа объекта объект наследует шаблон ведомости. Оба
            стоят после спецодежды: за ними заходят реже, а заводят их один раз в начале. */}
        {can(права, 'core.dictionary.view') && <СправочникТиповОформления />}

        {can(права, 'core.dictionary.view') && <СправочникТиповОбъектов />}

        {can(права, 'core.user.view') && <UsersPanel />}

        {/* Жалобы на переписку. Пришли из приложения: человек выбрал сообщение и нажал
            «Пожаловаться». Стоят рядом с людьми и доступами намеренно — решение по жалобе
            чаще всего про человека, а не про текст. */}
        {can(права, 'core.content_report.manage') && <ЖалобыНаПереписку />}

        {/* Ответ на настоящий вопрос человека — «а где категории трат» — остался, а рамка
          вокруг него ушла. Блок с заголовком и одной фразой «здесь ничего нет» — это
          поверхность, которой никто не просил: заголовок обещает раздел настроек, а внутри
          объяснение, почему его не будет.
          Номер блока работ и неделя по нашему плану убраны отсюда ещё раньше: это внутренняя
          кухня исполнителя, и человеку в кабинете она ничего не объясняет. */}
        <p className='max-w-prose text-paragraph-sm text-text-sub-600'>
          Категории трат, состояния строк и типы дел настраиваются данными, а не экранами: их
          заводит администратор системы по заявке.
        </p>

        {/* Номер версии. Одна строка внизу, и вот зачем она нужна.
            13.09.2026 заказчик написал «иконки мобильного приложения появились спасибо, но код
            по прежнему не видно», и выяснить, какая сборка у него стоит, было НЕЧЕМ: номер не
            показывался нигде, кроме полосы обновления, которая живёт секунду. Разговор шёл
            вслепую — мы про новый экран, он про старый. Теперь он называет число, и дальше
            разговор идёт о деле.
            С 1.0.24 рядом с номером — история обновлений (просьба финансового директора от
            14.10.2026: «историю обновлений нужно оставить доступной в настройках, там же, где
            указана версия программы»). */}
        <СтрокаВерсии открыть={открытьИсторию} />
      </div>
    </main>
  );
}

function АдресСервера() {
  const где = useПлатформа();
  // Пустой адрес — «тот же, что у страницы»: в поле показываем адрес страницы, а не пустоту.
  const [адрес, setАдрес] = useState(serverAddress() || window.location.origin);
  const [проверяем, setПроверяем] = useState(false);
  const [итог, setИтог] = useState<{ ok: boolean; текст: string } | null>(null);

  async function сохранить() {
    setПроверяем(true);
    setИтог(null);
    try {
      // Проверяем ДО сохранения: сохранённая опечатка выбросит человека на экран входа с
      // «нет связи», и причину он будет искать не там.
      const ответ = await checkServerAddress(адрес);
      if (!ответ.ok) {
        setИтог({
          ok: false,
          текст: `Не сохранила: ${ответ.reason ?? 'сервер не ответил'}.`,
        });
        return;
      }
      setServerAddress(адрес);
      setИтог({
        ok: true,
        текст: 'Адрес сохранён. Программа начнёт ходить туда со следующего запроса.',
      });
    } finally {
      setПроверяем(false);
    }
  }

  return (
    // Поле ввода взято из дизайн-системы, а не набрано своей вёрсткой: своё отличалось от
    // всех остальных полей продукта высотой, радиусом и поведением подписи. Пояснение под
    // полем — штатная подсказка поля, а не отдельная серая строка под ним.
    <Block title='Адрес сервера' note='Куда программа ходит за данными'>
      <p className='max-w-prose pb-3 text-paragraph-sm text-text-sub-600'>
        {где === 'ipad'
          ? 'Здесь адрес только виден. Сменить его можно на экране входа, под полями имени и фразы: на планшете подходит только защищённый адрес, тот, что начинается с https.'
          : 'Меняется, когда сервер организации переехал: переустанавливать программу для этого не нужно.'}
      </p>
      {/* Кнопка стоит У ПОЛЯ, а не в заголовке блока. В заголовке она уезжала к правому краю
          экрана — тысяча сто точек от поля, которое сохраняет, — и связь «эта кнопка про этот
          адрес» приходилось угадывать. Место действия в заголовке блока хорошо для действия
          над всем блоком (выгрузка списка), а не над единственным полем в нём. */}
      {/* На планшете адрес показывается, но НЕ меняется.
          Приложение из магазина собрано на один адрес, и другого у него быть не может: на
          планшете система запрещает обращения по открытому протоколу, и введённый вручную
          «http://192.168.1.10:8000» дал бы «нет связи» при живом сервере — без единой
          подсказки, в чём дело. Поправить это человек оттуда уже не смог бы: экран настроек
          лежит за входом, который к тому моменту перестал работать. */}
      {где === 'ipad' ? (
        <FieldList>
          <FieldRow label='Адрес' value={адрес} missing='адрес не задан' />
        </FieldList>
      ) : (
        <div className='flex max-w-md items-start gap-2'>
          <div className='min-w-0 flex-1'>
            <TextField
              id='settings-server-address'
              label='Адрес'
              value={адрес}
              onChange={setАдрес}
              icon={RiServerLine}
              placeholder='bark.example.ru или 192.168.1.10:8000'
              hint={
                serverAddressIsCustom()
                  ? 'Адрес задан вручную.'
                  : 'Сейчас действует адрес, с которым программа была собрана.'
              }
            />
          </div>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='small'
            className='mt-6 shrink-0'
            onClick={() => void сохранить()}
          >
            {проверяем ? 'Проверяем связь…' : 'Проверить и сохранить'}
          </Button.Root>
        </div>
      )}
      {итог === null ? null : итог.ok ? (
        <span className='mt-3 flex items-center gap-1 text-paragraph-xs text-success-base'>
          <RiCheckLine className='size-3.5' />
          {итог.текст}
        </span>
      ) : (
        <div className='mt-3'>
          <Hint.Root>{итог.текст}</Hint.Root>
        </div>
      )}
    </Block>
  );
}

/**
 * Переключатель представлений раздела.
 *
 * Показывается, только когда представлений правда два: у роли без права на журнал полоса из
 * одной кнопки — это орган управления, который ничего не переключает.
 */
function ПереключательПредставлений({
  текущее,
  виденЖурнал,
}: {
  текущее: string;
  виденЖурнал: boolean;
}) {
  const навигация = useNavigate();

  return (
    // Поле 16 в узком окне — то же правило, что у полосы представлений «Расчёта».
    <div className='flex shrink-0 items-center gap-2 border-b border-stroke-soft-200 bg-bg-white-0 px-5 py-2 узко:px-4'>
      <TabMenu.Root
        value={текущее}
        onValueChange={(следующее) => {
          if (следующее !== текущее) {
            void навигация(`/settings/${следующее}`);
          }
        }}
      >
        <TabMenu.List className='h-10 gap-5 border-t-0'>
          <TabMenu.Trigger value={VIEW_GENERAL}>Общие</TabMenu.Trigger>
          {виденЖурнал && <TabMenu.Trigger value={VIEW_AUDIT}>Журнал действий</TabMenu.Trigger>}
        </TabMenu.List>
      </TabMenu.Root>
    </div>
  );
}
